import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError, formatDate } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  Search, Plus, Sparkles, Trash2, FileText, ListChecks, CheckCircle2, Archive, Clock,
} from "lucide-react";
import { toast } from "sonner";

const STATUS_META = {
  queued:         { label: "Queued",         cls: "bg-[#F3F4F6] text-[#374151] border-[#E5E7EB]", icon: Clock },
  outline_ready:  { label: "Outline ready",  cls: "bg-[#EFF6FF] text-[#1D4ED8] border-[#BFDBFE]", icon: ListChecks },
  draft_ready:    { label: "Draft ready",    cls: "bg-[#FEF3C7] text-[#92400E] border-[#FDE68A]", icon: FileText },
  promoted:       { label: "Promoted",       cls: "bg-[#ECFDF5] text-[#047857] border-[#A7F3D0]", icon: CheckCircle2 },
  archived:       { label: "Archived",       cls: "bg-[#FEE2E2] text-[#991B1B] border-[#FECACA]", icon: Archive },
};

const PRIORITY_META = {
  P0: { label: "P0", cls: "bg-[#B91C1C] text-white" },
  P1: { label: "P1", cls: "bg-[#0066FF] text-white" },
  P2: { label: "P2", cls: "bg-[#6B7280] text-white" },
};

export default function AdminBlogTopicsIndex() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState({ topics: [], total: 0, categories: [], status_counts: {} });
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newTitle, setNewTitle] = useState("");

  const status = params.get("status") || "all";
  const category = params.get("category") || "all";
  const priority = params.get("priority") || "all";
  const qParam = params.get("q") || "";
  const [searchInput, setSearchInput] = useState(qParam);
  useEffect(() => { setSearchInput(qParam); }, [qParam]);

  const setParam = (k, v) => {
    const next = new URLSearchParams(params);
    if (!v || v === "all") next.delete(k); else next.set(k, v);
    setParams(next, { replace: true });
  };

  useEffect(() => {
    const t = setTimeout(() => {
      if (searchInput !== qParam) setParam("q", searchInput.trim());
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [searchInput]);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status, category, priority, qParam]);

  const load = async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams({ limit: "300" });
      if (qParam) qs.set("q", qParam);
      if (status !== "all") qs.set("status", status);
      if (category !== "all") qs.set("category", category);
      if (priority !== "all") qs.set("priority", priority);
      const { data } = await api.get(`/operations/blog/topics?${qs.toString()}`);
      setData(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const createQuick = async () => {
    if (!newTitle.trim()) return;
    setCreating(true);
    try {
      const { data } = await api.post(`/operations/blog/topics`, {
        title: newTitle.trim(),
        target_keyword: "",
        category: "Guide",
        target_word_count: 1200,
        priority: "P1",
      });
      toast.success("Topic added to queue");
      setNewTitle("");
      navigate(`/admin/blog/topics/${data.id}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setCreating(false);
    }
  };

  const remove = async (topic, e) => {
    e.stopPropagation();
    if (!window.confirm(`Remove "${topic.title}" from the queue?`)) return;
    try {
      await api.delete(`/operations/blog/topics/${topic.id}`);
      toast.success("Topic removed");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const counts = useMemo(() => ({
    total: data.total,
    queued: data.status_counts.queued || 0,
    outline_ready: data.status_counts.outline_ready || 0,
    draft_ready: data.status_counts.draft_ready || 0,
    promoted: data.status_counts.promoted || 0,
  }), [data]);

  return (
    <div className="p-6 md:p-8 max-w-[1500px] mx-auto" data-testid="admin-topics-index">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <div className="uppercase-label">Content Roadmap</div>
          <h1 className="font-display text-3xl font-semibold tracking-tight text-[#0A2540]">Topic Queue</h1>
          <div className="text-sm text-[#6B7280] mt-1.5 flex flex-wrap items-baseline gap-x-2">
            <span><span className="font-medium text-[#0A2540] tabular-nums">{counts.total}</span> total</span>
            <span>·</span>
            <span><span className="font-medium text-[#374151] tabular-nums">{counts.queued}</span> queued</span>
            <span>·</span>
            <span><span className="font-medium text-[#1D4ED8] tabular-nums">{counts.outline_ready}</span> outlines</span>
            <span>·</span>
            <span><span className="font-medium text-[#92400E] tabular-nums">{counts.draft_ready}</span> drafts</span>
            <span>·</span>
            <span><span className="font-medium text-[#047857] tabular-nums">{counts.promoted}</span> promoted</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Input
            placeholder="Quick add: paste a topic title…"
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && createQuick()}
            className="h-9 w-[340px]"
            data-testid="topics-quick-add-input"
          />
          <Button onClick={createQuick} disabled={creating || !newTitle.trim()} className="bg-[#0066FF] hover:bg-[#0052CC] h-9" data-testid="topics-quick-add-btn">
            <Plus className="w-3.5 h-3.5 mr-1.5" /> Add topic
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border border-[#E5E7EB] rounded-lg p-3 mb-4 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" />
          <Input
            placeholder="Search by title, keyword, or notes…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="pl-9 h-9"
            data-testid="topics-search"
          />
        </div>
        <Select value={status} onValueChange={(v) => setParam("status", v)}>
          <SelectTrigger className="h-9 w-[160px]" data-testid="topics-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {Object.entries(STATUS_META).map(([k, m]) => (<SelectItem key={k} value={k}>{m.label}</SelectItem>))}
          </SelectContent>
        </Select>
        <Select value={priority} onValueChange={(v) => setParam("priority", v)}>
          <SelectTrigger className="h-9 w-[120px]" data-testid="topics-priority-filter"><SelectValue placeholder="Priority" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All priorities</SelectItem>
            <SelectItem value="P0">P0</SelectItem>
            <SelectItem value="P1">P1</SelectItem>
            <SelectItem value="P2">P2</SelectItem>
          </SelectContent>
        </Select>
        <Select value={category} onValueChange={(v) => setParam("category", v)}>
          <SelectTrigger className="h-9 w-[200px]" data-testid="topics-category-filter"><SelectValue placeholder="Category" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            {data.categories.map((c) => (<SelectItem key={c} value={c}>{c}</SelectItem>))}
          </SelectContent>
        </Select>
        {(qParam || status !== "all" || category !== "all" || priority !== "all") && (
          <Button variant="ghost" className="h-9" onClick={() => setParams({})} data-testid="topics-clear-filters">Clear</Button>
        )}
      </div>

      <div className="bg-white border border-[#E5E7EB] rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F9FAFB] border-b border-[#E5E7EB]">
              <tr className="text-left text-[11px] uppercase tracking-wider text-[#6B7280]">
                <th className="px-4 py-2.5 font-medium w-12">Pri</th>
                <th className="px-4 py-2.5 font-medium">Topic</th>
                <th className="px-4 py-2.5 font-medium">Category</th>
                <th className="px-4 py-2.5 font-medium">Status</th>
                <th className="px-4 py-2.5 font-medium text-right">Words</th>
                <th className="px-4 py-2.5 font-medium">Last run</th>
                <th className="px-4 py-2.5 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={7} className="p-10 text-center text-[#6B7280]" data-testid="topics-loading">Loading topics…</td></tr>}
              {!loading && data.topics.length === 0 && (
                <tr><td colSpan={7} className="p-10 text-center text-[#6B7280]" data-testid="topics-empty">
                  No topics match these filters.
                </td></tr>
              )}
              {!loading && data.topics.map((t) => {
                const sm = STATUS_META[t.status] || STATUS_META.queued;
                const pm = PRIORITY_META[t.priority] || PRIORITY_META.P2;
                const Icon = sm.icon;
                return (
                  <tr
                    key={t.id}
                    data-testid={`topic-row-${t.id}`}
                    onClick={() => navigate(`/admin/blog/topics/${t.id}`)}
                    className="border-b border-[#F3F4F6] hover:bg-[#F9FAFB] transition-colors cursor-pointer"
                  >
                    <td className="px-4 py-2.5">
                      <span className={`inline-flex items-center justify-center w-7 h-5 text-[10px] rounded ${pm.cls}`}>{pm.label}</span>
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="text-[#0A2540] font-medium truncate max-w-[560px]">{t.title}</div>
                      {t.target_keyword ? (
                        <div className="text-[11px] text-[#9CA3AF] truncate max-w-[560px]">kw: {t.target_keyword}</div>
                      ) : null}
                    </td>
                    <td className="px-4 py-2.5 text-[#374151]">{t.category}</td>
                    <td className="px-4 py-2.5">
                      <span className={`inline-flex items-center gap-1 px-2 h-5 text-[10px] uppercase tracking-wider rounded border ${sm.cls}`}>
                        <Icon className="w-3 h-3" /> {sm.label}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-right tabular-nums text-[#6B7280]">
                      {t.draft_word_count ? t.draft_word_count.toLocaleString() : `~${t.target_word_count}`}
                    </td>
                    <td className="px-4 py-2.5 text-[#6B7280] tabular-nums">
                      {t.last_generated_at ? formatDate(t.last_generated_at) : "—"}
                    </td>
                    <td className="px-4 py-2.5 text-right" onClick={(e) => e.stopPropagation()}>
                      <Link
                        to={`/admin/blog/topics/${t.id}`}
                        title="Open generation workspace"
                        data-testid={`topic-open-${t.id}`}
                        className="inline-flex items-center justify-center h-7 px-2 rounded text-xs text-[#0066FF] hover:bg-[#EFF6FF]"
                      >
                        <Sparkles className="w-3.5 h-3.5 mr-1" /> Generate
                      </Link>
                      <button
                        onClick={(e) => remove(t, e)}
                        title="Delete topic"
                        data-testid={`topic-delete-${t.id}`}
                        className="inline-flex items-center justify-center h-7 w-7 rounded text-[#9CA3AF] hover:text-[#B91C1C] hover:bg-[#FEF2F2]"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
